import React, { useEffect, useState, useRef, useMemo } from 'react';
import api, { absUrl } from '../../services/api';
import { useChatStore } from '../../hooks/useChatStore';

export default function ChannelInfoPanel({ chatId, user, onClose }) {
  const [chatInfo, setChatInfo] = useState(null);
  const [sharedFiles, setSharedFiles] = useState({ images: [], videos: [], audio: [], files: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [mediaTab, setMediaTab] = useState('images');
  const [rssFeeds, setRssFeeds] = useState([]);
  const [rssUrl, setRssUrl] = useState('');
  const [rssBusy, setRssBusy] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [nameBusy, setNameBusy] = useState(false);
  const { deleteChat, unsubscribeChannel, removeChannelParticipant, renameChat } = useChatStore();
  const setActiveChat = useChatStore((s) => s.setActiveChat);
  const chatMessages = useChatStore((s) => s.messages?.[chatId]);

  const visibleFiles = useMemo(() => {
    const deletedIds = new Set(
      (chatMessages || []).filter((m) => m?.isDeleted).map((m) => m.id)
    );
    if (deletedIds.size === 0) return sharedFiles;
    const notDeleted = (f) => !f?.messageId || !deletedIds.has(f.messageId);
    return {
      images: (sharedFiles.images || []).filter(notDeleted),
      videos: (sharedFiles.videos || []).filter(notDeleted),
      audio: (sharedFiles.audio || []).filter(notDeleted),
      files: (sharedFiles.files || []).filter(notDeleted),
    };
  }, [sharedFiles, chatMessages]);

  const saveName = async () => {
    const name = (nameValue || '').trim();
    if (!name || nameBusy) return;
    setNameBusy(true);
    setError('');
    try {
      await renameChat(chatId, name);
      setEditingName(false);
      await load();
    } catch (err) {
      setError('Не удалось переименовать');
    }
    setNameBusy(false);
  };

  const loadRss = async () => {
    try {
      const res = await api.get(`/channels/${chatId}/rss-feeds`);
      setRssFeeds(res.data || []);
    } catch (e) {
      setRssFeeds([]);
    }
  };

  const addRssFeed = async () => {
    const url = rssUrl.trim();
    if (!url || rssBusy) return;
    setRssBusy(true);
    setError('');
    try {
      await api.post(`/channels/${chatId}/rss-feeds`, { url });
      setRssUrl('');
      await loadRss();
    } catch (err) {
      setError('Не удалось подключить ленту: ' + (err.response?.data?.message || err.message));
    }
    setRssBusy(false);
  };

  const removeRssFeed = async (feedId) => {
    if (!window.confirm('Отключить RSS-ленту?')) return;
    setRssBusy(true);
    try {
      await api.delete(`/channels/${chatId}/rss-feeds/${feedId}`);
      await loadRss();
    } catch (err) {
      setError('Не удалось отключить ленту');
    }
    setRssBusy(false);
  };

  const gotoMessage = (f) => {
    if (f?.messageId) {
      window.__pendingMsg = { chatId, messageId: f.messageId };
      setActiveChat(chatId);
      onClose();
      setTimeout(() => window.dispatchEvent(new Event('scrollToMessage')), 150);
    } else {
      window.open(absUrl(f.fileUrl), '_blank');
    }
  };
  const fileInputRef = useRef(null);

  const load = async () => {
    try {
      const [chatRes, filesRes] = await Promise.all([
        api.get(`/chats/${chatId}`),
        api.get(`/chats/${chatId}/shared-files`),
      ]);
      setChatInfo(chatRes.data);
      setSharedFiles(filesRes.data || { images: [], videos: [], audio: [], files: [] });
    } catch (e) {
      setError('Не удалось загрузить информацию о канале');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [chatId]);

  useEffect(() => { if (chatId) loadRss(); }, [chatId]);

  const isAdmin = chatInfo && (
    chatInfo.createdBy === user?.id ||
    chatInfo.participants?.some((p) => p.userId === user?.id && p.role === 'ADMIN')
  );

  const handleAvatarFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBusy(true);
    setError('');
    const formData = new FormData();
    formData.append('file', file);
    try {
      await api.post(`/chats/${chatId}/avatar`, formData);
      await load();
    } catch (err) {
      setError('Не удалось загрузить аватарку');
    }
    setBusy(false);
    e.target.value = '';
  };

  const handleDelete = async () => {
    if (!window.confirm('Удалить канал? Это действие нельзя отменить.')) return;
    setBusy(true);
    try {
      await deleteChat(chatId);
      onClose();
    } catch (err) {
      setError('Ошибка удаления канала');
    }
    setBusy(false);
  };

  const handleUnsubscribe = async () => {
    if (!window.confirm('Отписаться от канала?')) return;
    setBusy(true);
    try {
      await unsubscribeChannel(chatId);
      onClose();
    } catch (err) {
      setError('Не удалось отписаться от канала');
    }
    setBusy(false);
  };

  const handleRemoveParticipant = async (targetUserId) => {
    setBusy(true);
    try {
      await removeChannelParticipant(chatId, targetUserId);
      await load();
    } catch (err) {
      setError('Не удалось удалить участника');
    }
    setBusy(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal user-info-modal" onClick={(e) => e.stopPropagation()}>
        <div className="user-info-header">
          <div className="user-info-avatar channel-info-avatar">
            {chatInfo?.avatarUrl ? (
              <img src={absUrl(chatInfo.avatarUrl)} alt="" className="user-info-avatar" />
            ) : '📢'}
          </div>
          <div className="user-info-titles">
            {editingName ? (
              <div className="rename-row">
                <input
                  type="text"
                  value={nameValue}
                  onChange={(e) => setNameValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') saveName(); }}
                  maxLength={100}
                  autoFocus
                />
                <button className="action-btn rename-save" onClick={saveName} disabled={nameBusy || !nameValue.trim()}>
                  {nameBusy ? '...' : '✓'}
                </button>
                <button className="action-btn rename-cancel" onClick={() => setEditingName(false)}>✕</button>
              </div>
            ) : (
              <h3>{chatInfo?.name || 'Канал'}
                {isAdmin && (
                  <button
                    className="rename-edit-btn"
                    onClick={() => { setNameValue(chatInfo?.name || ''); setEditingName(true); }}
                    title="Переименовать"
                  >✏️</button>
                )}
              </h3>
            )}
            <span className="user-info-username">
              {(chatInfo?.visibility === 'PRIVATE' ? '🔒 Личный' : '🌍 Публичный')}
              {chatInfo?.allowComments ? ' · 💬 комментарии' : ' · 🔇 без комментариев'}
            </span>
            <span className="user-info-email">
              {chatInfo?.participants?.length || 0} подписчик(ов)
            </span>
          </div>
          <button className="modal-close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="user-info-body">
          {loading && <div className="sticker-picker-loading">Загрузка...</div>}
          {error && <div className="search-error">{error}</div>}
          {!loading && (
            <>
              {isAdmin && (
                <div className="channel-admin-actions" style={{ marginBottom: '12px' }}>
                  <button
                    className="action-btn"
                    style={{ width: '100%' }}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={busy}
                  >
                    {busy ? 'Загрузка...' : 'Изменить аватарку'}
                  </button>
                  <input type="file" ref={fileInputRef} hidden accept="image/*" onChange={handleAvatarFile} />
                </div>
              )}
              <div className="rss-section" style={{ marginBottom: '12px' }}>
                <h4>📡 RSS-ленты</h4>
                {isAdmin && (
                  <div className="rss-add-row">
                    <input
                      type="text"
                      placeholder="https://example.com/rss.xml"
                      value={rssUrl}
                      onChange={(e) => setRssUrl(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') addRssFeed(); }}
                      disabled={rssBusy}
                    />
                    <button className="action-btn" onClick={addRssFeed} disabled={rssBusy || !rssUrl.trim()}>
                      {rssBusy ? '...' : 'Добавить'}
                    </button>
                  </div>
                )}
                {rssFeeds.length === 0 ? (
                  <div className="user-info-status muted" style={{ marginTop: '6px' }}>
                    {isAdmin
                      ? 'Ленты не подключены. Элементы ленты будут публиковаться в канале автоматически.'
                      : 'Ленты не подключены.'}
                  </div>
                ) : (
                  rssFeeds.map((f) => (
                    <div key={f.id} className="rss-feed-row">
                      <div className="rss-feed-info">
                        <span className="rss-feed-title" title={f.title || f.url}>{f.title || f.url}</span>
                        <a className="rss-feed-url" href={f.url} target="_blank" rel="noopener noreferrer" title={f.url} onClick={(e) => e.stopPropagation()}>{f.url}</a>
                      </div>
                      {isAdmin && (
                        <button
                          className="action-btn"
                          style={{ padding: '2px 8px', fontSize: '12px', flexShrink: 0 }}
                          onClick={() => removeRssFeed(f.id)}
                          disabled={rssBusy}
                          title="Отключить ленту"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>
              <div className="shared-files-section">
                <h4>Подписчики ({chatInfo?.participants?.length || 0})</h4>
                <div className="scheduled-item" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                  {chatInfo?.participants?.map((p) => (
                    <div key={p.id} className="channel-subscriber">
                      {p.avatarUrl ? (
                        <img src={absUrl(p.avatarUrl)} alt="" className="avatar-sm" />
                      ) : (
                        <div className="avatar-sm subscriber-avatar-placeholder">{p.displayName?.[0] || p.username?.[0]}</div>
                      )}
                      <span className="channel-subscriber-name">{p.displayName || p.username}</span>
                      {p.role === 'ADMIN' && <span className="channel-admin-badge">админ</span>}
                      {isAdmin && p.userId !== user?.id && p.role !== 'ADMIN' && (
                        <button
                          className="action-btn"
                          style={{ marginLeft: 'auto', padding: '2px 8px', fontSize: '12px' }}
                          onClick={() => handleRemoveParticipant(p.userId)}
                          disabled={busy}
                          title="Удалить из канала"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <div className="media-tabs">
                {['images', 'videos', 'audio', 'files'].map((t) => (
                  <button
                    key={t}
                    className={`media-tab ${mediaTab === t ? 'active' : ''}`}
                    onClick={() => setMediaTab(t)}
                  >
                    {t === 'images' ? 'Изображения' : t === 'videos' ? 'Видео' : t === 'audio' ? 'Аудио' : 'Файлы'}
                    <span className="media-tab-count">{visibleFiles[t]?.length || 0}</span>
                  </button>
                ))}
              </div>
              {mediaTab === 'images' && (visibleFiles.images?.length > 0 ? (
                <div className="shared-files-images">
                  {visibleFiles.images.slice(0, 24).map((f) => (
                    <img key={f.id} src={absUrl(f.fileUrl)} alt={f.fileName} className="shared-file-img"
                      title={f.fileName} onClick={() => gotoMessage(f)} />
                  ))}
                </div>
              ) : (
                <div className="user-info-status muted">Нет изображений</div>
              ))}
              {mediaTab === 'videos' && (visibleFiles.videos?.length > 0 ? (
                <div className="shared-files-list">
                  {visibleFiles.videos.slice(0, 10).map((f) => (
                    <a key={f.id} className="shared-file-link" href="#!" onClick={(e) => { e.preventDefault(); gotoMessage(f); }}>
                    {f.fileName}
                    </a>
                  ))}
                </div>
              ) : (
                <div className="user-info-status muted">Нет видео</div>
              ))}
              {mediaTab === 'audio' && (visibleFiles.audio?.length > 0 ? (
                <div className="shared-files-list">
                  {visibleFiles.audio.slice(0, 10).map((f) => (
                    <a key={f.id} className="shared-file-link" href="#!" onClick={(e) => { e.preventDefault(); gotoMessage(f); }}>
                    {f.fileName}
                    </a>
                  ))}
                </div>
              ) : (
                <div className="user-info-status muted">Нет аудио</div>
              ))}
              {mediaTab === 'files' && (visibleFiles.files?.length > 0 ? (
                <div className="shared-files-list">
                  {visibleFiles.files.slice(0, 15).map((f) => (
                    <a key={f.id} className="shared-file-link" href="#!" onClick={(e) => { e.preventDefault(); gotoMessage(f); }}>
                    {f.fileName} <span className="shared-file-size">({Math.round(f.fileSize / 1024)} KB)</span>
                    </a>
                  ))}
                </div>
              ) : (
                <div className="user-info-status muted">Нет файлов</div>
              ))}
              {isAdmin ? (
                <button className="user-info-btn danger" onClick={handleDelete} disabled={busy} style={{ width: '100%', marginTop: '12px' }}>
                  {busy ? '...' : 'Удалить канал'}
                </button>
              ) : (
                <button className="user-info-btn danger" onClick={handleUnsubscribe} disabled={busy} style={{ width: '100%', marginTop: '12px' }}>
                  {busy ? '...' : 'Отписаться от канала'}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}